import { CustomEventWithDetail, CustomEventWithoutDetail, html, View } from 'rune-ts';
import { SearchApiS } from '../../../../../features/Search/outbound/share';
import {
  InputSearch,
  InputSearchProps,
  SearchArrowKeydownEvent,
  SearchDebounceInputEvent,
  SearchInputEvent,
  SearchSubmitEvent,
} from '../../../atoms/InputSearch/InputSearch';
import { AutoCompleteController } from '../controllers/AutoCompleteController.ts';
import {
  AutoCompleteClickEvent,
  AutoCompleteList,
  AutoCompleteListData,
  AutoCompleteListOption,
} from './AutoCompleteList';
import klass from './AutoCompleteSearchInput.module.scss';

export class NotEmptySubmitEvent extends CustomEventWithDetail<string> {}
export class EmptySubmitEvent extends CustomEventWithoutDetail {}
export class AutoCompleteListHideEvent extends CustomEventWithoutDetail {}
export class AutoCompleteListShowEvent extends CustomEventWithoutDetail {}

type AutoCompleteSearchInputProps = InputSearchProps &
  Pick<AutoCompleteListOption, 'highlight'> & {
    container_klass?: string;
  };

export class AutoCompleteSearchInput extends View<AutoCompleteSearchInputProps> {
  container_klass: Readonly<string>;

  InputSearchView: InputSearch;
  AutoCompleteListView: AutoCompleteList;
  AutoCompleteController: AutoCompleteController;

  constructor(data: AutoCompleteSearchInputProps) {
    super(data);
    this.container_klass = data.container_klass ?? '';
    this.InputSearchView = new InputSearch(this.data);
    this.AutoCompleteListView = new AutoCompleteList({
      klass: {
        list: klass.auto_complete_list,
      },
      option: {
        highlight: data.highlight,
      },
    });

    this.AutoCompleteController = new AutoCompleteController(
      {
        search: data.value,
      },
      {
        onRequestAutoComplete: this.onRequestAutoComplete,
      },
      {
        minCharsForAutoComplete: 2,
        customCondition(_, alreadySubmitted) {
          return !alreadySubmitted;
        },
      },
    );
  }

  override template() {
    return html`<div class="${klass.auto_complete_input_container} ${this.container_klass}">
      ${this.InputSearchView}${this.AutoCompleteListView}
    </div>`;
  }

  override onMount() {
    this.delegate(SearchInputEvent, InputSearch, (e) => {
      this.setValue(e.detail);

      // 자동완성이 가능한 경우는 `SearchDebounceInputEvent` 에서 처리하므로 즉시 종료
      if (this.AutoCompleteController.canAutoComplete()) {
        return;
      }
      // 자동완성 안 되는 경우는 자동완성 목록 가림
      this.AutoCompleteController.cancelPrevRequest();
      this.hideAutoCompleteList();
    });

    this.delegate(SearchDebounceInputEvent, InputSearch, async (e) => {
      const auto_completes = await this.AutoCompleteController.handleAutoComplete();

      if (auto_completes) {
        this.AutoCompleteListView.setData({ auto_completes, value: e.detail });
        this.AutoCompleteListView.redraw();

        if (auto_completes.length) {
          this.dispatchEvent(AutoCompleteListShowEvent, { bubbles: true });
        } else {
          this.dispatchEvent(AutoCompleteListHideEvent, { bubbles: true });
        }
      }
    });

    this.delegate(AutoCompleteClickEvent, AutoCompleteList, (e) => {
      const search = e.detail;
      if (!search) {
        return;
      }

      // 검색어 자동완성 즉시 숨김
      this.hideAutoCompleteList();
      // 이전 호출 있다면 취소
      this.AutoCompleteController.cancelPrevRequest();

      this.InputSearchView.setValue(search);
      this.dispatchEvent(NotEmptySubmitEvent, { bubbles: true, detail: search });
    });

    // submit
    this.delegate(SearchSubmitEvent, InputSearch, async (e) => {
      if (!e.detail) {
        this.dispatchEvent(EmptySubmitEvent, { bubbles: true });
        return;
      }

      // 검색어 자동완성 즉시 숨김
      this.hideAutoCompleteList();
      // 이전 호출 있다면 취소
      this.AutoCompleteController.cancelPrevRequest();

      let search: string = e.detail;
      const focused_el = this.AutoCompleteListView.getFocusedEl();
      if (focused_el?.dataset?.search) {
        search = focused_el.dataset.search;
      }
      this.InputSearchView.setValue(search);
      this.dispatchEvent(NotEmptySubmitEvent, { bubbles: true, detail: search });
    });

    // key up/down event
    this.delegate(SearchArrowKeydownEvent, InputSearch, (e) => {
      const key = e.detail;
      if (key === 'ArrowUp') {
        this.AutoCompleteListView.moveFocusUp();
      } else if (key === 'ArrowDown') {
        this.AutoCompleteListView.moveFocusDown();
      }
    });
  }

  setData(data: AutoCompleteListData) {
    this.AutoCompleteListView.setData(data);
  }

  focus() {
    this.InputSearchView.focus();
  }

  private hideAutoCompleteList() {
    this.AutoCompleteListView.hide();
    this.dispatchEvent(AutoCompleteListHideEvent, { bubbles: true });
  }

  setInputValue(value: string) {
    this.InputSearchView.setValue(value);
  }

  getInputValue() {
    return this.InputSearchView.getValue();
  }

  private setValue(value?: string) {
    this.data.value = value;
    this.AutoCompleteController.setSearch(value);
  }

  private async onRequestAutoComplete(value?: string) {
    try {
      const res = await SearchApiS.getAutoCompleteListApi({ search: value });
      return res.auto_completes;
    } catch (e) {
      return [];
    }
  }
}
